import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../webux/webux-layouts/WebUxPage';
import { Button, Space, Divider, Modal, Spin } from 'antd';
import './profile.less';
import { useConnection, useUserInfo } from '../../connection/Application';
import { FlexBox } from '../../components/atoms/FlexBox';
import { EditOutlined } from '@ant-design/icons';
import Title from 'antd/lib/typography/Title';
import { InfoView, InfoViewItem } from '../../components/info-view/InfoView';
import { FormModal, useFormModal } from '../../components/form-modal/FormModal';
import Form, { FormSchema } from '../../form/Form';
import { Employee } from '../../entity/Employee';
import Utils from '../../utils/Utils';
import { UserAvatar, UserNameView } from '../../components/atoms/UserAvatar';
import { PageHeader } from '@ant-design/pro-layout';

const roles = [
  { id: 'admin', value: 'Admin' },
  { id: 'hr', value: 'HR' },
  { id: 'management', value: 'Management' },
  { id: 'user', value: 'User' },
];

const editSchema: FormSchema = [
  { id: 'image', name: '', type: 'avatar' },
  {
    id: 'firstName',
    name: 'First Name',
    type: 'string',
  },
  {
    id: 'lastName',
    name: 'Last Name',
    type: 'string',
  },
];

const passwordSchema: FormSchema = [
  {
    id: 'currentPassword',
    name: 'Current Password',
    type: 'password',
    options: {
      rules: [{ required: true, whitespace: true, message: 'Password is required' }],
    },
    layout: { md: 24 },
  },
  {
    id: 'newPassword',
    name: 'New Password',
    type: 'password',
    options: {
      rules: [{ required: true, whitespace: true, message: 'Password is required' }],
    },
  },
  {
    id: 'reTypePassword',
    name: 'Re-Type Password',
    type: 'password',
    options: {
      dependencies: ['newPassword'],
      rules: [
        { required: true, whitespace: true, message: 'Password is required' },
        { validator: 'equals', to: 'newPassword', message: 'Same password required' },
      ],
    },
  },
];

const Profile: FC = () => {
  const [userData, setUserData] = useState<Employee>();
  const [loading, setLoading] = useState<boolean>(false);
  const user = useUserInfo();
  const connection = useConnection();
  const edituser = useFormModal<Employee>();
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      setLoading(true);
      connection
        .get(`users/${user.userId}`)
        .then((response) => {
          const responseData = response;
          delete responseData.id;
          setUserData(responseData);
        })
        .finally(() => setLoading(false));
    }
  }, [connection, user]);

  const onEdit = useCallback(
    async (formData) => {
      try {
        setLoading(true);
        const requestData = { ...userData };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setUserData({
          ...userData,
          firstName: formData.firstName,
          lastName: formData.lastName,
          image: formData.image,
        });
        requestData.firstName = formData.firstName;
        requestData.lastName = formData.lastName;
        requestData.image = formData.image;
        delete requestData.employeeId;
        delete requestData.designation;
        delete requestData.goalHolderId;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete requestData?.name;
        delete requestData.teams;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete requestData?.teams1;
        delete requestData?.roles;
        delete requestData?.supervisors;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete requestData?.delete;

        const res = await connection.patch(`users/${user?.userId}`, {
          user: Utils.removeNull(requestData),
        });
        if (res.session) {
          connection.setSession(res.session);
        }

        setLoading(false);
        edituser.close();
      } catch (e: any) {
        if (e.body?.body?.errors) {
          edituser.setErrors(e.body.body.errors);
        } else {
          edituser.setErrors([]);
        }
        setLoading(false);
      }
    },
    [connection, edituser, user?.userId, userData],
  );

  const changePassword = useCallback(
    (value) => {
      delete value.reTypePassword;
      setLoading(true);
      connection
        .patch(`users/password-change/${user?.userId}`, value)
        .then(() => {
          form.resetFields();
          Modal.success({
            title: 'Password changed successfully',
          });
        })
        .catch((error) => {
          form.setFields([{ name: 'currentPassword', errors: [error.message] }]);
        })
        .finally(() => setLoading(false));
    },
    [connection, form, user?.userId],
  );

  return (
    <WebUxPage>
      <PageHeader ghost={false} style={{ background: '#FFF' }} title="Profile" />
      <WebUxPageContent>
        <WebUxPageItem>
          <Spin spinning={loading}>
            <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
              <FlexBox horizontalAlign={'right'}>
                <Button
                  icon={<EditOutlined />}
                  key="1"
                  type="primary"
                  onClick={() => edituser.open(userData)}>
                  Edit Profile
                </Button>
              </FlexBox>
              <FlexBox horizontalAlign={'center'} direction={'vertical'}>
                <UserAvatar src={user?.image} size={150} />
                <Title>
                  {user && (userData?.firstName ?? '') + ' ' + (userData?.lastName ?? '')}
                </Title>
              </FlexBox>
              <Divider />
              <div className="profile-item-main">
                <div className="profile-item" style={{ flex: 2 }}>
                  <InfoView className="info-view" style={{ marginLeft: '10%' }}>
                    <InfoViewItem title={'Email'}>{userData && userData.email}</InfoViewItem>
                    {/*<InfoViewItem title={'Employee Id'}>*/}
                    {/*  {userData && userData.employeeId}*/}
                    {/*</InfoViewItem>*/}
                    <InfoViewItem title={'EPF Number'}>{userData && userData.epfNo}</InfoViewItem>
                    <InfoViewItem title={'Designation'}>
                      {userData && userData.designation}{' '}
                    </InfoViewItem>
                    <InfoViewItem title="Date of Join">{userData && userData.doj}</InfoViewItem>
                    <InfoViewItem title="Supervisor">
                      {userData?.supervisors?.length !== 0
                        ? userData?.supervisors?.map((value1, index) => (
                            <UserNameView
                              src={value1.image}
                              name={`${value1.firstName} ${value1.lastName}`}
                              tags={true}
                              key={index}
                            />
                          ))
                        : 'No Supervisor'}
                    </InfoViewItem>
                    <InfoViewItem title="Teams">
                      {userData?.teams?.map((value1, index) => (
                        <UserNameView
                          position={value1?.position}
                          src={value1.image}
                          name={value1.name}
                          tags={true}
                          key={index}
                        />
                      ))}
                    </InfoViewItem>
                    <InfoViewItem title="Roles">
                      {userData?.roles?.map((value1, index) => (
                        <UserNameView
                          name={roles.find((e) => e.id == value1)?.value ?? value1}
                          tags={true}
                          key={index}
                        />
                      ))}
                    </InfoViewItem>
                  </InfoView>
                </div>
                {process.env.REACT_APP_MSAL !== 'true' && (
                  <>
                    <Divider className="password-section" />
                    <div className="profile-item" style={{ flex: 1 }}>
                      <Space size={10} direction={'vertical'}>
                        <Title level={3}>Change Password</Title>
                        <Form layout={'vertical'} form={form} onFinish={changePassword}>
                          <Form.Items schema={passwordSchema} />
                          <Form.Item>
                            <Button type={'primary'} onClick={form.submit}>
                              Save
                            </Button>
                          </Form.Item>
                        </Form>
                      </Space>
                    </div>
                  </>
                )}
              </div>
              <Divider />
              <FlexBox direction={'vertical'} horizontalAlign={'center'} />
            </Space>
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
      <FormModal title={'Edit Profile'} form={edituser} onSubmit={onEdit}>
        <Form.Items schema={editSchema} />
      </FormModal>
    </WebUxPage>
  );
};

export default Profile;
